/**
 * The pose solver: turns the interpolated simulation view into joint angles
 * every frame. Pose targets are a weighted blend of authored poses plus
 * procedural terms (arm swing from the run phase, torso lean from the body);
 * each joint tracks its target with a critically damped spring, so a new
 * target is approached from wherever the joint is. Two-bone IK pins the feet
 * to the ground and the hands to the bar or the reach point through eased
 * targets, and feeds the springs so IK can engage and disengage without a
 * step. The skeleton is solved in the body frame (facing right) and mapped
 * to the world by scaling x through the facing blend, so a turnaround reads
 * as a turn. Nothing here allocates per frame.
 */

import { HZ, S, type Sim, SolidClass } from "@lache/sim";
import { halfLifeFactor, type RenderFeel } from "../feel";
import type { ViewState } from "../view";
import { defaultPoses, POSE_KEYS, POSE_NAMES, type Pose, type PoseName } from "./poses";
import { angleDelta, BONES, type IkResult, twoBoneIk } from "./skeleton";

const DEG = Math.PI / 180;
const NJ = POSE_KEYS.length; // hipY + 10 angles
const J = {
  hipY: 0,
  torso: 1,
  head: 2,
  shoulderL: 3,
  elbowL: 4,
  shoulderR: 5,
  elbowR: 6,
  hipL: 7,
  kneeL: 8,
  hipR: 9,
  kneeR: 10,
} as const;
const W: Record<PoseName, number> = {} as Record<PoseName, number>;
POSE_NAMES.forEach((n, i) => {
  W[n] = i;
});

/** World-space skeleton points produced each frame. */
export class SkeletonPoints {
  hipX = 0;
  hipY = 0;
  neckX = 0;
  neckY = 0;
  headX = 0;
  headY = 0;
  headTopX = 0;
  headTopY = 0;
  headBackX = 0;
  headBackY = 0;
  shoulderX = 0;
  shoulderY = 0;
  elbowLX = 0;
  elbowLY = 0;
  handLX = 0;
  handLY = 0;
  elbowRX = 0;
  elbowRY = 0;
  handRX = 0;
  handRY = 0;
  kneeLX = 0;
  kneeLY = 0;
  footLX = 0;
  footLY = 0;
  kneeRX = 0;
  kneeRY = 0;
  footRX = 0;
  footRY = 0;
  toeLX = 0;
  toeLY = 0;
  toeRX = 0;
  toeRY = 0;
  /** World torso axis angle (radians from vertical, positive toward +x). */
  torsoAngle = 0;
  headAngle = 0;
  /** Facing blend applied this frame (-1..1). */
  fb = 1;
}

/** Body-frame point list: [hip, neck, head, headTop, headBack, shoulder, elbowL, handL, elbowR, handR, kneeL, footL, kneeR, footR, toeL, toeR]. */
const BP = {
  hip: 0,
  neck: 2,
  head: 4,
  headTop: 6,
  headBack: 8,
  shoulder: 10,
  elbowL: 12,
  handL: 14,
  elbowR: 16,
  handR: 18,
  kneeL: 20,
  footL: 22,
  kneeR: 24,
  footR: 26,
  toeL: 28,
  toeR: 30,
} as const;

/** Cubic Hermite basis. */
function hermite(q: number, p0: number, p1: number, m0: number, m1: number): number {
  const q2 = q * q;
  const q3 = q2 * q;
  return (2 * q3 - 3 * q2 + 1) * p0 + (q3 - 2 * q2 + q) * m0 + (-2 * q3 + 3 * q2) * p1 + (q3 - q2) * m1;
}

export class PoseSolver {
  poses: Record<PoseName, Pose>;
  readonly weights = new Float64Array(POSE_NAMES.length);
  /** Spring state in pose space (radians, hipY in meters). */
  readonly target = new Float64Array(NJ);
  readonly cur = new Float64Array(NJ);
  readonly vel = new Float64Array(NJ);
  /** Body-frame limb angles drawn this frame: [upperArmL, forearmL, upperArmR, forearmR, thighL, shinL, thighR, shinR]. */
  readonly limbAngles = new Float64Array(8);
  readonly points = new SkeletonPoints();
  private readonly bp = new Float64Array(32);
  /** Per-foot planted weight (1 in stance), eased. */
  private readonly footPlant = new Float64Array(2);
  /** Hip bounce from the gait this frame, m. */
  gaitBounce = 0;
  /** Eased blend weights for the feet (gait vs idle, brake stance) and the arm swing, so a stop
   * or a start moves the limbs through the springs instead of switching their targets. */
  private gaitWEased = 0;
  private brakeEased = 0;
  private armSwingEased = 0;
  /** Eased IK targets in the body frame: footL(x,y) footR handL handR. */
  private readonly ikTargets = new Float64Array(8);
  private ikInit = false;
  private readonly ik: IkResult = { a: 0, flex: 0 };
  private runPhase = 0;
  private lastFsign = 0;
  private lastDistance = 0;
  /** Pose editor: force a pose (weight 1) when set. */
  previewPose: PoseName | null = null;
  /** Ring buffer of joint angles per frame for tests. */
  readonly traceSize = 600;
  readonly trace: Float32Array[] = [];
  /** Frame time of each trace row, s, so a pop test can tell a stalled frame from a jump. */
  readonly traceDt = new Float32Array(600);
  private traceHead = 0;
  traceCount = 0;
  private initialized = false;
  /** Seconds since the hands left a box (the vault's landing pose), large when none. */
  private sincePlant = 1e9;
  /** Seconds the feet have been planted on the ground (reset by a flight, a slide, or a roll). */
  private groundedSeconds = 0;
  /** Eased weight of the slide pose. */
  private slideW = 0;
  /** How long the feet take to pin: quick after a landing, slower out of a slide or a roll, whose
   * poses put the legs far from a stance. */
  private legPinSeconds = 0.08;
  /** Progress of a mantle over its ledge, 0 at the hang to 1 with the feet on top (0 when not mantling). */
  private mantleP = 0;

  constructor(poses: Record<PoseName, Pose> = defaultPoses) {
    this.poses = poses;
    for (let i = 0; i < this.traceSize; i++) this.trace.push(new Float32Array(NJ + 9));
  }

  private snapToTargets(): void {
    for (let i = 0; i < NJ; i++) {
      this.cur[i] = this.target[i]!;
      this.vel[i] = 0;
    }
  }

  private computeWeights(view: ViewState, sim: Sim, feel: RenderFeel): void {
    const w = this.weights;
    w.fill(0);
    if (this.previewPose) {
      w[W[this.previewPose]] = 1;
      return;
    }
    const t = sim.tuning;
    const tuck = view.tuck;
    const speed = Math.abs(view.vx);
    if (view.state === S.Roll) {
      w[W.roll] = 1;
      return;
    }
    this.mantleP = 0;
    if (view.ledgeSolid >= 0) {
      // Hanging from a ledge: the catch pose over the attach ease, then the hang.
      const catchW = Math.max(0, 1 - view.stateTicks / (0.08 * HZ));
      w[W.catch] = catchW;
      w[W.ledgeHang] = 1 - catchW;
      return;
    }
    if (view.planted !== 0 && sim.state.plantBox >= 0 && view.mantling === 1) {
      // A mantle: from the hang to the pull over the edge as the feet rise from the hang's depth
      // under the ledge to its top.
      const top = sim.world.solidY1[sim.state.plantBox]!;
      const depth = t.armLength + t.torsoOffset + t.standHeight * 0.5;
      const p = Math.min(1, Math.max(0, 1 - (top - view.y) / depth));
      this.mantleP = p;
      w[W.ledgeHang] = 1 - p;
      w[W.mantle] = p;
      return;
    }
    if (view.planted !== 0 && sim.state.plantBox >= 0) {
      // A vault or a hop over a box. Before the face the approach carries into the plant (hands reaching
      // for the edge, knees coming up); across the box the plant turns into the clear; once the hands
      // let go the clear holds until the landing pose takes over.
      const box = sim.state.plantBox;
      const x0 = sim.world.solidX0[box]!;
      const x1 = sim.world.solidX1[box]!;
      const r = t.capsuleRadius;
      const across =
        view.facing > 0 ? (view.x + r - x0) / (x1 - x0 + 2 * r) : (x1 - (view.x - r)) / (x1 - x0 + 2 * r);
      const p = Math.min(1, Math.max(0, across));
      if (sim.state.plantHands === 1) {
        w[W.vaultPlant] = 1 - p;
        w[W.vaultClear] = p;
      } else {
        // The hop over a knee-high box: the legs tuck up and reach through.
        w[W.airTuck] = 1 - p;
        w[W.vaultClear] = p;
      }
      return;
    }
    if (view.barId >= 0) {
      const sinceAttach = view.stateTicks;
      const catchW = Math.max(0, 1 - sinceAttach / (0.08 * HZ));
      const omegaRef = 3.0;
      const fw = view.omega > 0 ? Math.min(1, view.omega / omegaRef) : 0;
      const bw = view.omega < 0 ? Math.min(1, -view.omega / omegaRef) : 0;
      let rest = 1 - tuck;
      w[W.airTuck] = tuck;
      const c = catchW * rest;
      w[W.catch] = c;
      rest -= c;
      w[W.swingForward] = fw * rest;
      w[W.swingBack] = bw * rest;
      w[W.deadHang] = rest - w[W.swingForward]! - w[W.swingBack]!;
      return;
    }
    if (view.grounded === 1 && view.state !== S.Fail && view.state !== S.Roll) {
      const runW = Math.min(1, speed / 2.5);
      // Speeding up or slowing down is relative to the velocity, not the facing: a reversal skids
      // while already facing the other way.
      const moveSign = view.vx < 0 ? -1 : 1;
      const speedRate = view.ax * moveSign;
      const accelW = Math.min(1, Math.max(0, speedRate / t.runAccel)) * 0.7 * runW;
      const brakeW = Math.min(1, Math.max(0, -speedRate / t.stopDecel)) * Math.min(1, speed / t.skidSpeed);
      const absorbW = Math.min(1, Math.max(0, view.absorb / feel.landingMax) * 1.5);
      const sinceLandS = view.ticksSinceLand / HZ;
      const stabW = sinceLandS > 0.12 && sinceLandS < 0.45 ? Math.min(1, (0.45 - sinceLandS) / 0.2) * 0.6 : 0;
      const reachW = view.armsOut === 1 ? Math.min(1, view.reach) : 0;
      // The vault tell: the arms reach down toward a box whose window is coming up.
      let approachW = 0;
      if (speed > 1) {
        const dir = view.vx < 0 ? -1 : 1;
        const box = sim.world.nextObstacleAhead(
          view.x,
          view.y,
          t.capsuleRadius,
          t.standHeight,
          dir,
          SolidClass.Box,
          t.vaultLookahead,
        );
        if (box >= 0) {
          const height = sim.world.solidY1[box]! - view.y;
          if (height > t.hurdleStepHeight && height <= t.hurdleMaxHeight) {
            const toFaceMs = (sim.world.queryDist / speed) * 1000;
            approachW = Math.min(1, Math.max(0, (t.vaultWindowMs * 1.5 - toFaceMs) / t.vaultWindowMs));
          }
        }
      }
      let rest = 1;
      // The slide lies over everything else on the ground, eased in and out so its long, leaned-back
      // shape never switches on or off in a frame.
      w[W.slide] = this.slideW * rest;
      rest -= w[W.slide]!;
      w[W.crouch] = tuck * rest;
      rest -= w[W.crouch]!;
      w[W.vaultApproach] = approachW * rest * 0.8;
      rest -= w[W.vaultApproach]!;
      w[W.absorb] = absorbW * rest;
      rest -= w[W.absorb]!;
      w[W.prepareLache] = reachW * rest * 0.8;
      rest -= w[W.prepareLache]!;
      w[W.brake] = brakeW * rest;
      rest -= w[W.brake]!;
      w[W.accelerate] = accelW * rest;
      rest -= w[W.accelerate]!;
      w[W.stabilize] = stabW * rest;
      rest -= w[W.stabilize]!;
      w[W.run] = runW * rest;
      w[W.idle] = rest - w[W.run]!;
      return;
    }
    // Airborne
    const sinceRelease = view.ticksSinceRelease;
    const jumpW = view.state === S.Jump ? Math.max(0, 1 - view.stateTicks / (0.06 * HZ)) : 0;
    const releaseW = sinceRelease < 0.06 * HZ ? 1 - sinceRelease / (0.06 * HZ) : 0;
    const flyW =
      sinceRelease < 0.6 * HZ ? Math.max(0, 1 - Math.max(0, sinceRelease - 0.06 * HZ) / (0.5 * HZ)) : 0;
    const reachW = view.armsOut === 1 ? 1 : 0;
    const nearBar = view.assistBar >= 0 ? 1 : 0;
    let landW = 0;
    if (view.vy < -2) {
      const r = t.capsuleRadius;
      const ground = sim.world.probeDown(view.x - r, view.x + r, view.y, 1.2);
      if (ground > Number.NEGATIVE_INFINITY) {
        const dist = view.y - ground;
        landW = Math.min(1, Math.max(0, (1.2 - dist) / 0.9));
      }
    }
    let rest = 1;
    // Just off a box: reach for the landing.
    const vaultLandW = Math.max(0, 1 - this.sincePlant / 0.4);
    w[W.vaultLand] = vaultLandW * rest;
    rest -= w[W.vaultLand]!;
    w[W.airTuck] = tuck * rest;
    rest -= w[W.airTuck]!;
    w[W.jump] = jumpW * rest;
    rest -= w[W.jump]!;
    w[W.release] = releaseW * rest;
    rest -= w[W.release]!;
    const reachPart = reachW * rest;
    w[W.reach] = reachPart * nearBar;
    w[W.prepareLache] = reachPart * (1 - nearBar);
    rest -= reachPart;
    w[W.landHigh] = landW * rest * (1 - reachW);
    rest -= w[W.landHigh]!;
    w[W.flyToNext] = flyW * rest;
    rest -= w[W.flyToNext]!;
    w[W.airExtend] = rest;
  }

  private blendTargets(): void {
    const tg = this.target;
    tg.fill(0);
    const w = this.weights;
    for (let p = 0; p < POSE_NAMES.length; p++) {
      const wp = w[p]!;
      if (wp <= 0) continue;
      const pose = this.poses[POSE_NAMES[p]!];
      tg[J.hipY] = tg[J.hipY]! + wp * pose.hipY;
      tg[J.torso] = tg[J.torso]! + wp * pose.torso * DEG;
      tg[J.head] = tg[J.head]! + wp * pose.head * DEG;
      tg[J.shoulderL] = tg[J.shoulderL]! + wp * pose.shoulderL * DEG;
      tg[J.elbowL] = tg[J.elbowL]! + wp * pose.elbowL * DEG;
      tg[J.shoulderR] = tg[J.shoulderR]! + wp * pose.shoulderR * DEG;
      tg[J.elbowR] = tg[J.elbowR]! + wp * pose.elbowR * DEG;
      tg[J.hipL] = tg[J.hipL]! + wp * pose.hipL * DEG;
      tg[J.kneeL] = tg[J.kneeL]! + wp * pose.kneeL * DEG;
      tg[J.hipR] = tg[J.hipR]! + wp * pose.hipR * DEG;
      tg[J.kneeR] = tg[J.kneeR]! + wp * pose.kneeR * DEG;
    }
  }

  private springHz(feel: RenderFeel, j: number): number {
    switch (j) {
      case J.hipY:
      case J.torso:
        return feel.springTorsoHz;
      case J.head:
        return feel.springHeadHz;
      case J.shoulderL:
      case J.shoulderR:
      case J.elbowL:
      case J.elbowR:
        return feel.springArmsHz;
      default:
        return feel.springLegsHz;
    }
  }

  private integrateSprings(feel: RenderFeel, dt: number): void {
    // Critically damped: x'' = -w^2 (x - target) - 2 w x'. Semi-implicit, substepped for stability.
    const sub = dt > 0.012 ? Math.ceil(dt / 0.008) : 1;
    const h = dt / sub;
    for (let j = 0; j < NJ; j++) {
      const wn = 2 * Math.PI * this.springHz(feel, j);
      let x = this.cur[j]!;
      let v = this.vel[j]!;
      const tgt = this.target[j]!;
      for (let k = 0; k < sub; k++) {
        const a = -wn * wn * (x - tgt) - 2 * wn * v;
        v += a * h;
        x += v * h;
      }
      this.cur[j] = x;
      this.vel[j] = v;
    }
  }

  private easeTarget(i: number, x: number, y: number, k: number): void {
    const t = this.ikTargets;
    t[i] = t[i]! + (x - t[i]!) * k;
    t[i + 1] = t[i + 1]! + (y - t[i + 1]!) * k;
  }

  /** Move the tip point `i` toward the target along the joint-to-target line, within the stretch limit, by weight w. */
  private stretchTo(
    i: number,
    jointI: number,
    tx: number,
    ty: number,
    len: number,
    stretch: number,
    w: number,
  ): void {
    if (w <= 0) return;
    const bp = this.bp;
    const jx = bp[jointI]!;
    const jy = bp[jointI + 1]!;
    const dx = tx - jx;
    const dy = ty - jy;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d < 1e-6) return;
    let l = d;
    if (l > len * (1 + stretch)) l = len * (1 + stretch);
    if (l < len * (1 - stretch)) l = len * (1 - stretch);
    const sx = jx + (dx / d) * l;
    const sy = jy + (dy / d) * l;
    bp[i] = bp[i]! + (sx - bp[i]!) * w;
    bp[i + 1] = bp[i + 1]! + (sy - bp[i + 1]!) * w;
  }

  /** Body-frame FK for both limb pairs from the current spring angles (or overrides). */
  private limbPoint(i: number, fromI: number, angle: number, len: number): void {
    const bp = this.bp;
    bp[i] = bp[fromI]! + Math.sin(angle) * len;
    bp[i + 1] = bp[fromI + 1]! - Math.cos(angle) * len;
  }

  update(view: ViewState, sim: Sim, feel: RenderFeel, dt: number): void {
    const t = sim.tuning;
    const slideTarget = view.state === S.Slide ? view.tuck : 0;
    this.slideW = view.snapped
      ? slideTarget
      : this.slideW + (slideTarget - this.slideW) * halfLifeFactor(60, dt);
    if (view.planted !== 0 || view.snapped) this.sincePlant = view.planted !== 0 ? 0 : 1e9;
    else if (this.sincePlant < 1e9) this.sincePlant = view.grounded === 1 ? 1e9 : this.sincePlant + dt;
    this.computeWeights(view, sim, feel);
    this.blendTargets();
    const w = this.weights;
    const fb = view.facingBlend;
    if (view.barId >= 0 && !this.previewPose) {
      // On a bar the drawn shoulders sit the arm's length below the hands on the pendulum (L less the
      // arm is the torso offset the tuck shortens), so a tuck does not push the shoulders toward the bar
      // and bend the elbows, and extending again does not snap them straight.
      const shoulderAbove = view.L - t.armLength;
      const bone = BONES.torso - 0.04;
      const pinned = view.comY + view.absorb + shoulderAbove - Math.cos(this.target[J.torso]!) * bone;
      const k = view.attachProgress;
      this.target[J.hipY] = this.target[J.hipY]! + (pinned - this.target[J.hipY]!) * k;
    }
    const fsign = fb < 0 ? -1 : 1;
    // Direction of travel in the body frame: +1 when moving the way the character faces.
    const moveDir = view.vx === 0 ? 1 : (view.vx < 0 ? -1 : 1) * fsign;
    // When the facing flips, mirror the eased contact targets so feet and hands keep their world
    // positions while the body turns over them, instead of swapping sides.
    if (this.lastFsign !== 0 && fsign !== this.lastFsign) {
      const it0 = this.ikTargets;
      for (let i = 0; i < 8; i += 2) it0[i] = -it0[i]!;
      // Mirror the hip swing angles too (the legs are pinned to those targets), so the legs keep
      // their world configuration instead of snapping to the mirrored one. Arms and torso follow
      // body-frame pose targets and are left alone.
      for (const j of [J.hipL, J.hipR]) {
        this.cur[j] = -this.cur[j]!;
        this.vel[j] = -this.vel[j]!;
      }
    }
    this.lastFsign = fsign;
    const preview = this.previewPose !== null;
    const attached = view.barId >= 0;
    // A ledge holds the hands the way a bar does: pinned to the ledge over the attach ease.
    const hanging = view.ledgeSolid >= 0;
    // A rolling body is curled and turning over its contact: no foot plant, like a flight. A slide keeps
    // the feet on the ground; the slide's contact targets below take over from the stance.
    const grounded = view.grounded === 1 && !attached && view.state !== S.Roll;
    const fresh = !this.initialized || view.snapped;
    if (!grounded) this.legPinSeconds = view.grounded === 1 ? 0.4 : 0.08;
    this.groundedSeconds = grounded ? (fresh ? 1 : this.groundedSeconds + dt) : 0;

    // Gait phase and the procedural run terms on top of the blended targets.
    const speed = Math.abs(view.vx);
    const speedFrac = Math.min(1, speed / t.maxRunSpeed);
    const stride = feel.strideBase + feel.stridePerSpeed * speed;
    // Integrate the phase from the distance travelled so a changing stride never jumps the feet.
    if (fresh) this.lastDistance = view.groundDistance;
    const dDist = view.groundDistance - this.lastDistance;
    this.lastDistance = view.groundDistance;
    this.runPhase += dDist / stride;
    this.runPhase -= Math.floor(this.runPhase);
    let stance = feel.stanceA / Math.max(0.5, speed) + feel.stanceB;
    if (stance > feel.stanceWalk) stance = feel.stanceWalk;
    if (stance < feel.stanceSprint) stance = feel.stanceSprint;
    // No running pattern while sliding against the facing (a reversal skid): the feet brake, planted.
    const forward = moveDir > 0 ? 1 : 0;
    // No running pattern in a slide: the legs lie on the slide's contacts.
    const gaitWRaw = grounded ? Math.min(1, speed / feel.gaitBlendSpeed) * forward * (1 - this.slideW) : 0;
    const runWRaw = (w[W.run]! + w[W.accelerate]! * 0.6) * forward;
    const kBlend = halfLifeFactor(feel.legBlendHalfLife, dt);
    if (fresh) {
      this.gaitWEased = gaitWRaw;
      this.brakeEased = w[W.brake]!;
      this.armSwingEased = runWRaw;
    } else {
      this.gaitWEased += (gaitWRaw - this.gaitWEased) * kBlend;
      this.brakeEased += (w[W.brake]! - this.brakeEased) * kBlend;
      this.armSwingEased += (runWRaw - this.armSwingEased) * kBlend;
    }
    const gaitW = this.gaitWEased;
    const runW = this.armSwingEased;
    if (runW > 0) {
      const amp = (feel.armSwingBase + feel.armSwingPerSpeed * speedFrac) * DEG;
      // The left arm swings forward with the right leg: the right foot swings forward while the left
      // foot is planted (phase 0 to stance), so the left arm leads at phase ~0.25 plus the lead.
      const sw = Math.sin((this.runPhase - feel.armPhase) * 2 * Math.PI);
      const center = (feel.armCenter + feel.armCenterPerSpeed * speedFrac) * DEG;
      this.target[J.shoulderL] = this.target[J.shoulderL]! + runW * (center + sw * amp);
      this.target[J.shoulderR] = this.target[J.shoulderR]! + runW * (center - sw * amp);
      const elbowL = (feel.elbowBase + feel.elbowSwing * (0.5 + 0.5 * sw)) * DEG;
      const elbowR = (feel.elbowBase + feel.elbowSwing * (0.5 - 0.5 * sw)) * DEG;
      this.target[J.elbowL] = this.target[J.elbowL]! + runW * (elbowL - this.target[J.elbowL]!);
      this.target[J.elbowR] = this.target[J.elbowR]! + runW * (elbowR - this.target[J.elbowR]!);
      // Lean grows with speed squared (upright jog, committed sprint) and pitches forward a little at
      // each foot strike.
      const pitch = feel.gaitPitch * DEG * speedFrac * Math.cos(4 * Math.PI * this.runPhase);
      this.target[J.torso] =
        this.target[J.torso]! + runW * (speedFrac * speedFrac * feel.runLean * DEG + pitch);
    }
    // The brake pose leans against the motion; on a reversal skid that is toward the new facing.
    if (moveDir < 0 && w[W.brake]! > 0) {
      this.target[J.torso] = this.target[J.torso]! - 2 * w[W.brake]! * this.poses.brake.torso * DEG;
    }
    // The head counters the torso lean so the face stays level.
    this.target[J.head] =
      this.target[J.head]! - feel.headLevel * (this.target[J.torso]! + view.torsoLean * fsign);
    // Hip bounce: lowest at each mid-stance, highest in flight. Applied after the springs.
    this.gaitBounce =
      -feel.gaitBounce *
      speedFrac *
      gaitW *
      0.5 *
      (1 + Math.cos(4 * Math.PI * (this.runPhase - stance * 0.5)));

    // Body frame: origin at the center of mass, +x forward. Whole-body rotation and the facing
    // squash are applied when mapping to the world.
    const bodyAngle = view.bodyAngle;
    const cosB = Math.cos(bodyAngle);
    const sinB = Math.sin(bodyAngle);
    const comX = view.x;
    const comY = view.y + view.comY;
    // World -> body: rotate by -bodyAngle, then mirror x by the facing sign.
    const toBodyX = (wx: number, wy: number): number => ((wx - comX) * cosB - (wy - comY) * sinB) * fsign;
    const toBodyY = (wx: number, wy: number): number => (wx - comX) * sinB + (wy - comY) * cosB;

    const kIk = halfLifeFactor(feel.ikHalfLife, dt);
    // Foot plant state fades fast, so a lifting toe leaves the ground at once.
    const kPlant = halfLifeFactor(8, dt);
    const it = this.ikTargets;
    if (fresh) {
      this.initialized = true;
      this.snapToTargets();
      this.ikInit = false;
    }

    // IK target easing (body frame), then IK results override the spring targets so the springs
    // carry the limbs into and out of contact without a step.
    const cur = this.cur;
    const tg = this.target;
    // Torso (needed for the shoulder position used by the arm IK). Ground lean is torso-only.
    const hipOff = tg[J.hipY]! - view.absorb - view.comY;

    if (!this.ikInit) {
      this.ikInit = true;
      it[0] = -0.12;
      it[1] = -view.comY;
      it[2] = 0.12;
      it[3] = -view.comY;
      it[4] = it[6] = 0.1;
      it[5] = it[7] = hipOff + BONES.torso - BONES.upperArm - BONES.forearm;
    }

    // Shoulder position estimate from the current spring state (for IK).
    const curTorso = cur[J.torso]! + view.torsoLean * fsign;
    // On a bar the hip height is pinned to its target after the springs (below), so the arm IK reads the target.
    const hipForIk = attached
      ? cur[J.hipY]! + (tg[J.hipY]! - cur[J.hipY]!) * view.attachProgress
      : cur[J.hipY]!;
    const curHipOff = hipForIk - view.absorb - view.comY + this.gaitBounce;
    const shX = Math.sin(curTorso) * (BONES.torso - 0.04);
    const shY = curHipOff + Math.cos(curTorso) * (BONES.torso - 0.04);

    // Arms: reaching drives the spring targets; attached pins through the springs then directly.
    // The hands go to the bar while attached and to the box edge while planted.
    // Vaulting, the hands reach for the edge and hold it through the plant half, letting go over the clear.
    const planted = view.planted !== 0 && sim.state.plantHands === 1;
    // Mantling, the hands hold the ledge until the body is half way over it, then let go.
    const plantHold = planted
      ? view.mantling === 1
        ? Math.min(1, Math.max(0, 2 * (1 - this.mantleP)))
        : Math.min(1, 2 * w[W.vaultPlant]!)
      : 0;
    const reachW = preview
      ? 0
      : attached || hanging
        ? 1
        : planted
          ? plantHold
          : view.armsOut === 1
            ? Math.min(1, view.reach)
            : 0;
    let armDirect = 0;
    if (reachW > 0) {
      const hx = toBodyX(view.handX, view.handY);
      const hy = toBodyY(view.handX, view.handY);
      // During a rung lift the player already holds the moving bar. Following its contact
      // directly avoids making the wrists trail behind as the duck shortens the pendulum.
      const k = view.ladderClimbing ? 1 : attached || hanging ? Math.min(1, kIk * 1.6) : kIk;
      this.easeTarget(4, hx - 0.02 * fsign, hy, k);
      this.easeTarget(6, hx + 0.02 * fsign, hy, k);
      for (let side = 0; side < 2; side++) {
        const ti = 4 + side * 2;
        twoBoneIk(
          shX,
          shY,
          it[ti]!,
          it[ti + 1]!,
          BONES.upperArm,
          BONES.forearm,
          -1,
          this.ik,
          feel.ikSoftArms,
        );
        const sJ = side === 0 ? J.shoulderL : J.shoulderR;
        const eJ = side === 0 ? J.elbowL : J.elbowR;
        // IK angles are relative to the torso axis; blend along the short way round so a
        // partial reach never passes through the seam at +-180 degrees.
        const a1 = tg[sJ]! + angleDelta(tg[sJ]!, this.ik.a - curTorso);
        tg[sJ] = tg[sJ]! + (a1 - tg[sJ]!) * reachW;
        tg[eJ] = tg[eJ]! + (this.ik.flex - tg[eJ]!) * reachW;
      }
      armDirect = attached || hanging ? view.attachProgress : 0;
    } else {
      // Park the eased targets on the drawn hands so a later reach starts from where the hands are.
      const a1L = curTorso + cur[J.shoulderL]!;
      const a2L = a1L + cur[J.elbowL]!;
      it[4] = shX + Math.sin(a1L) * BONES.upperArm + Math.sin(a2L) * BONES.forearm;
      it[5] = shY - Math.cos(a1L) * BONES.upperArm - Math.cos(a2L) * BONES.forearm;
      const a1R = curTorso + cur[J.shoulderR]!;
      const a2R = a1R + cur[J.elbowR]!;
      it[6] = shX + Math.sin(a1R) * BONES.upperArm + Math.sin(a2R) * BONES.forearm;
      it[7] = shY - Math.cos(a1R) * BONES.upperArm - Math.cos(a2R) * BONES.forearm;
    }

    // Legs: on the ground the gait's foot targets drive IK, which feeds the springs and, once
    // planted, the drawn angles directly (so feet never slide).
    let legDirect = 0;
    if (grounded && !preview) {
      const brakeW = this.brakeEased;
      const groundYb = -view.comY; // feet point in the body frame
      // Stance travel: contact ahead of the hip by stanceAhead of it, toe-off behind by the rest.
      const travel = stride * stance;
      const ahead = travel * feel.stanceAhead;
      const behind = travel - ahead;
      // Swing tangents: the foot keeps part of its ground-relative speed when it lifts off and when it
      // lands (paw-back), so contact never slaps.
      const tangent = -feel.swingTangent * stride * (1 - stance);
      const liftMax = feel.gaitLiftBase + feel.gaitLift * speedFrac;
      for (let side = 0; side < 2; side++) {
        let ph = this.runPhase + (side === 1 ? 0.5 : 0);
        if (ph >= 1) ph -= 1;
        let offset: number;
        let lift = 0;
        let planted = 0;
        if (ph < stance) {
          offset = ahead - (ph / stance) * travel;
          planted = 1;
        } else {
          const q = (ph - stance) / (1 - stance);
          // Forward travel runs on warped time so the foot passes under the hip early (knee drive).
          const qw = q ** feel.swingWarp;
          offset = hermite(qw, -behind, ahead, tangent, tangent);
          lift = liftMax * Math.sin(Math.PI * q) ** feel.liftShape;
        }
        const idleOffset = side === 0 ? -0.12 : 0.12;
        let ox = idleOffset + (offset - idleOffset) * gaitW;
        // Skid: both feet slide out toward the direction of motion (behind the facing on a reversal).
        ox += brakeW * (0.3 * moveDir - ox);
        this.easeTarget(side * 2, ox, groundYb + lift * gaitW, kIk);
        const plantTarget = gaitW > 0.05 ? planted : 1;
        this.footPlant[side] = this.footPlant[side]! + (plantTarget - this.footPlant[side]!) * kPlant;
        const ti = side * 2;
        twoBoneIk(0, curHipOff, it[ti]!, it[ti + 1]!, BONES.thigh, BONES.shin, 1, this.ik, feel.ikSoftLegs);
        const hJ = side === 0 ? J.hipL : J.hipR;
        const kJ = side === 0 ? J.kneeL : J.kneeR;
        // Out of a slide or a roll the legs ease from their pose onto the stance over the pin time.
        const legBlend =
          this.legPinSeconds > 0.08 ? Math.min(1, this.groundedSeconds / this.legPinSeconds) : 1;
        tg[hJ] = legBlend >= 1 ? this.ik.a : cur[hJ]! + angleDelta(cur[hJ]!, this.ik.a) * legBlend;
        tg[kJ] = legBlend >= 1 ? this.ik.flex : cur[kJ]! + angleDelta(cur[kJ]!, this.ik.flex) * legBlend;
      }
      // Feet pin over the first 80 ms of ground contact: after a landing, and after a slide or a roll,
      // whose poses let the legs go.
      legDirect = Math.min(1, this.groundedSeconds / this.legPinSeconds);
    } else if (!preview) {
      this.footPlant[0] = this.footPlant[0]! * (1 - kPlant);
      this.footPlant[1] = this.footPlant[1]! * (1 - kPlant);
      const a1L = cur[J.hipL]!;
      const a2L = a1L - cur[J.kneeL]!;
      it[0] = Math.sin(a1L) * BONES.thigh + Math.sin(a2L) * BONES.shin;
      it[1] = curHipOff - Math.cos(a1L) * BONES.thigh - Math.cos(a2L) * BONES.shin;
      const a1R = cur[J.hipR]!;
      const a2R = a1R - cur[J.kneeR]!;
      it[2] = Math.sin(a1R) * BONES.thigh + Math.sin(a2R) * BONES.shin;
      it[3] = curHipOff - Math.cos(a1R) * BONES.thigh - Math.cos(a2R) * BONES.shin;
    }

    // Slide: the body lies on the ground rather than on a pose alone. The lead leg reaches along the
    // ground, the trailing leg folds under the hip, and the trailing hand rests on the ground behind,
    // each through IK onto the ground line, blended in with the slide's weight so nothing sinks into it.
    if (this.slideW > 0.01 && !attached && !preview) {
      const gy = -view.comY;
      const k = this.slideW;
      // The eased foot targets the drawn shins stretch toward follow the slide's contacts too.
      it[0] = it[0]! + (feel.slideLeadReach - it[0]!) * k;
      it[1] = it[1]! + (gy + feel.shinRadius - it[1]!) * k;
      it[2] = it[2]! + (feel.slideTuckReach - it[2]!) * k;
      it[3] = it[3]! + (gy + feel.footRadius - it[3]!) * k;
      twoBoneIk(
        0,
        curHipOff,
        feel.slideLeadReach,
        gy + feel.shinRadius,
        BONES.thigh,
        BONES.shin,
        1,
        this.ik,
        1,
      );
      tg[J.hipL] = tg[J.hipL]! + (this.ik.a - tg[J.hipL]!) * k;
      tg[J.kneeL] = tg[J.kneeL]! + (this.ik.flex - tg[J.kneeL]!) * k;
      twoBoneIk(
        0,
        curHipOff,
        feel.slideTuckReach,
        gy + feel.footRadius,
        BONES.thigh,
        BONES.shin,
        1,
        this.ik,
        1,
      );
      tg[J.hipR] = tg[J.hipR]! + (this.ik.a - tg[J.hipR]!) * k;
      tg[J.kneeR] = tg[J.kneeR]! + (this.ik.flex - tg[J.kneeR]!) * k;
      if (reachW <= 0) {
        twoBoneIk(
          shX,
          shY,
          -feel.slideHandBehind,
          gy + feel.handRadius,
          BONES.upperArm,
          BONES.forearm,
          -1,
          this.ik,
          1,
        );
        const a1 = tg[J.shoulderL]! + angleDelta(tg[J.shoulderL]!, this.ik.a - curTorso);
        tg[J.shoulderL] = tg[J.shoulderL]! + (a1 - tg[J.shoulderL]!) * k;
        tg[J.elbowL] = tg[J.elbowL]! + (this.ik.flex - tg[J.elbowL]!) * k;
      }
    }

    // Every angle target is taken the short way round from where the joint is, so a target that
    // crosses +-180 degrees (arms overhead at a release) never sends a joint the long way.
    for (let j = 1; j < NJ; j++) tg[j] = cur[j]! + angleDelta(cur[j]!, tg[j]!);

    this.integrateSprings(feel, dt);

    // Direct IK pinning once planted or attached: pull the spring state onto the IK solution.
    if (legDirect > 0) {
      for (let side = 0; side < 2; side++) {
        const hJ = side === 0 ? J.hipL : J.hipR;
        const kJ = side === 0 ? J.kneeL : J.kneeR;
        cur[hJ] = cur[hJ]! + (tg[hJ]! - cur[hJ]!) * legDirect;
        cur[kJ] = cur[kJ]! + (tg[kJ]! - cur[kJ]!) * legDirect;
        if (legDirect >= 1) {
          this.vel[hJ] = 0;
          this.vel[kJ] = 0;
        }
      }
    }
    if (armDirect > 0) {
      // The shoulders' height on the pendulum is pinned with the arms, so the hip spring cannot lag the tuck.
      cur[J.hipY] = cur[J.hipY]! + (tg[J.hipY]! - cur[J.hipY]!) * armDirect;
      if (armDirect >= 1) this.vel[J.hipY] = 0;
      for (const j of [J.shoulderL, J.elbowL, J.shoulderR, J.elbowR]) {
        cur[j] = cur[j]! + (tg[j]! - cur[j]!) * armDirect;
        if (armDirect >= 1) this.vel[j] = 0;
      }
    }

    // Body-frame skeleton from the spring state.
    const bp = this.bp;
    const hipOffNow = cur[J.hipY]! - view.absorb - view.comY + this.gaitBounce;
    const torsoNow = cur[J.torso]! + view.torsoLean * fsign;
    bp[BP.hip] = 0;
    bp[BP.hip + 1] = hipOffNow;
    bp[BP.neck] = Math.sin(torsoNow) * BONES.torso;
    bp[BP.neck + 1] = hipOffNow + Math.cos(torsoNow) * BONES.torso;
    const headNow = torsoNow + cur[J.head]!;
    const hxs = Math.sin(headNow);
    const hys = Math.cos(headNow);
    bp[BP.head] = bp[BP.neck]! + hxs * (BONES.neck + BONES.headLen * 0.5);
    bp[BP.head + 1] = bp[BP.neck + 1]! + hys * (BONES.neck + BONES.headLen * 0.5);
    bp[BP.headTop] = bp[BP.neck]! + hxs * (BONES.neck + BONES.headLen);
    bp[BP.headTop + 1] = bp[BP.neck + 1]! + hys * (BONES.neck + BONES.headLen);
    bp[BP.headBack] = bp[BP.head]! - hys * BONES.headRadius;
    bp[BP.headBack + 1] = bp[BP.head + 1]! + hxs * BONES.headRadius;
    bp[BP.shoulder] = bp[BP.neck]! - Math.sin(torsoNow) * 0.04;
    bp[BP.shoulder + 1] = bp[BP.neck + 1]! - Math.cos(torsoNow) * 0.04;

    const la = this.limbAngles;
    la[0] = torsoNow + cur[J.shoulderL]!;
    la[1] = la[0]! + cur[J.elbowL]!;
    la[2] = torsoNow + cur[J.shoulderR]!;
    la[3] = la[2]! + cur[J.elbowR]!;
    la[4] = cur[J.hipL]!;
    la[5] = la[4]! - cur[J.kneeL]!;
    la[6] = cur[J.hipR]!;
    la[7] = la[6]! - cur[J.kneeR]!;
    this.limbPoint(BP.elbowL, BP.shoulder, la[0]!, BONES.upperArm);
    this.limbPoint(BP.handL, BP.elbowL, la[1]!, BONES.forearm);
    this.limbPoint(BP.elbowR, BP.shoulder, la[2]!, BONES.upperArm);
    this.limbPoint(BP.handR, BP.elbowR, la[3]!, BONES.forearm);
    this.limbPoint(BP.kneeL, BP.hip, la[4]!, BONES.thigh);
    this.limbPoint(BP.footL, BP.kneeL, la[5]!, BONES.shin);
    this.limbPoint(BP.kneeR, BP.hip, la[6]!, BONES.thigh);
    this.limbPoint(BP.footR, BP.kneeR, la[7]!, BONES.shin);
    // Rubber-hose contact: stretch the forearm and shin a little toward their eased targets so hands
    // stay on the bar and feet stay planted while the joint angles above stay smooth.
    this.stretchTo(BP.handL, BP.elbowL, it[4]!, it[5]!, BONES.forearm, feel.limbStretch, armDirect * reachW);
    this.stretchTo(BP.handR, BP.elbowR, it[6]!, it[7]!, BONES.forearm, feel.limbStretch, armDirect * reachW);
    this.stretchTo(
      BP.footL,
      BP.kneeL,
      it[0]!,
      it[1]!,
      BONES.shin,
      feel.limbStretch,
      legDirect * (grounded ? 1 : 0),
    );
    this.stretchTo(
      BP.footR,
      BP.kneeR,
      it[2]!,
      it[3]!,
      BONES.shin,
      feel.limbStretch,
      legDirect * (grounded ? 1 : 0),
    );
    // Feet: a planted foot keeps its toe on the ground ahead of the contact point, so the heel rises and
    // the foot rolls onto the toe at push-off; a swinging foot hangs from the shin.
    for (let side = 0; side < 2; side++) {
      const shin = side === 0 ? la[5]! : la[7]!;
      const planted = this.footPlant[side]!;
      const fi = side === 0 ? BP.footL : BP.footR;
      const ti = side === 0 ? BP.toeL : BP.toeR;
      let swingToe = -0.8 * shin;
      if (swingToe < -12 * DEG) swingToe = -12 * DEG;
      if (swingToe > 80 * DEG) swingToe = 80 * DEG;
      const hangX = bp[fi]! + Math.cos(swingToe) * feel.footLength;
      const hangY = bp[fi + 1]! - Math.sin(swingToe) * feel.footLength;
      // Planted toe: on the ground ahead of the eased contact point, never further than a stretched foot.
      const ii = side * 2;
      let toeX = it[ii]! + feel.footLength;
      let toeY = it[ii + 1]!;
      const dx = toeX - bp[fi]!;
      const dy = toeY - bp[fi + 1]!;
      const d = Math.sqrt(dx * dx + dy * dy);
      const maxD = feel.footLength * 1.25;
      if (d > maxD) {
        toeX = bp[fi]! + (dx / d) * maxD;
        toeY = bp[fi + 1]! + (dy / d) * maxD;
      }
      bp[ti] = hangX + (toeX - hangX) * planted;
      bp[ti + 1] = hangY + (toeY - hangY) * planted;
      // Never draw a toe below the ground while on it.
      if (grounded && bp[ti + 1]! < -view.comY) bp[ti + 1] = -view.comY;
    }

    // Map to the world: squash x by the facing blend, rotate by the body angle, offset by the COM.
    const P = this.points;
    P.fb = fb;
    const toWorldX = (x: number, y: number): number => comX + x * fb * cosB + y * sinB;
    const toWorldY = (x: number, y: number): number => comY - x * fb * sinB + y * cosB;
    P.hipX = toWorldX(bp[BP.hip]!, bp[BP.hip + 1]!);
    P.hipY = toWorldY(bp[BP.hip]!, bp[BP.hip + 1]!);
    P.neckX = toWorldX(bp[BP.neck]!, bp[BP.neck + 1]!);
    P.neckY = toWorldY(bp[BP.neck]!, bp[BP.neck + 1]!);
    P.headX = toWorldX(bp[BP.head]!, bp[BP.head + 1]!);
    P.headY = toWorldY(bp[BP.head]!, bp[BP.head + 1]!);
    P.headTopX = toWorldX(bp[BP.headTop]!, bp[BP.headTop + 1]!);
    P.headTopY = toWorldY(bp[BP.headTop]!, bp[BP.headTop + 1]!);
    P.headBackX = toWorldX(bp[BP.headBack]!, bp[BP.headBack + 1]!);
    P.headBackY = toWorldY(bp[BP.headBack]!, bp[BP.headBack + 1]!);
    P.shoulderX = toWorldX(bp[BP.shoulder]!, bp[BP.shoulder + 1]!);
    P.shoulderY = toWorldY(bp[BP.shoulder]!, bp[BP.shoulder + 1]!);
    P.elbowLX = toWorldX(bp[BP.elbowL]!, bp[BP.elbowL + 1]!);
    P.elbowLY = toWorldY(bp[BP.elbowL]!, bp[BP.elbowL + 1]!);
    P.handLX = toWorldX(bp[BP.handL]!, bp[BP.handL + 1]!);
    P.handLY = toWorldY(bp[BP.handL]!, bp[BP.handL + 1]!);
    P.elbowRX = toWorldX(bp[BP.elbowR]!, bp[BP.elbowR + 1]!);
    P.elbowRY = toWorldY(bp[BP.elbowR]!, bp[BP.elbowR + 1]!);
    P.handRX = toWorldX(bp[BP.handR]!, bp[BP.handR + 1]!);
    P.handRY = toWorldY(bp[BP.handR]!, bp[BP.handR + 1]!);
    P.kneeLX = toWorldX(bp[BP.kneeL]!, bp[BP.kneeL + 1]!);
    P.kneeLY = toWorldY(bp[BP.kneeL]!, bp[BP.kneeL + 1]!);
    P.footLX = toWorldX(bp[BP.footL]!, bp[BP.footL + 1]!);
    P.footLY = toWorldY(bp[BP.footL]!, bp[BP.footL + 1]!);
    P.kneeRX = toWorldX(bp[BP.kneeR]!, bp[BP.kneeR + 1]!);
    P.kneeRY = toWorldY(bp[BP.kneeR]!, bp[BP.kneeR + 1]!);
    P.footRX = toWorldX(bp[BP.footR]!, bp[BP.footR + 1]!);
    P.footRY = toWorldY(bp[BP.footR]!, bp[BP.footR + 1]!);
    P.toeLX = toWorldX(bp[BP.toeL]!, bp[BP.toeL + 1]!);
    P.toeLY = toWorldY(bp[BP.toeL]!, bp[BP.toeL + 1]!);
    P.toeRX = toWorldX(bp[BP.toeR]!, bp[BP.toeR + 1]!);
    P.toeRY = toWorldY(bp[BP.toeR]!, bp[BP.toeR + 1]!);
    // World angles of the torso and head axes (for the sash and headband): the squash shrinks x.
    P.torsoAngle =
      Math.atan2((bp[BP.neck]! - bp[BP.hip]!) * fb, bp[BP.neck + 1]! - bp[BP.hip + 1]!) + bodyAngle;
    P.headAngle =
      Math.atan2((bp[BP.headTop]! - bp[BP.neck]!) * fb, bp[BP.headTop + 1]! - bp[BP.neck + 1]!) + bodyAngle;

    // A respawn or restarted simulation deliberately snaps the pose. Its new timeline must not be
    // compared with the old attempt as though those were consecutive movement frames.
    if (view.snapped) {
      this.traceHead = 0;
      this.traceCount = 0;
    }
    // Trace (body-frame values).
    const row = this.trace[this.traceHead]!;
    for (let i = 0; i < NJ; i++) row[i] = cur[i]!;
    for (let i = 0; i < 8; i++) row[NJ + i] = la[i]!;
    // Facing sign last: a change between rows means the body frame mirrored, not that a joint moved.
    row[NJ + 8] = fsign;
    this.traceDt[this.traceHead] = dt;
    this.traceHead = (this.traceHead + 1) % this.traceSize;
    if (this.traceCount < this.traceSize) this.traceCount++;
  }

  /** The frame times of the last `n` trace rows, oldest first, s (allocates; tests only). */
  getTraceDt(n = this.traceSize): number[] {
    const count = Math.min(n, this.traceCount);
    const out: number[] = [];
    for (let i = count; i > 0; i--)
      out.push(this.traceDt[(this.traceHead - i + this.traceSize) % this.traceSize]!);
    return out;
  }

  /** Copy the last `n` trace rows (oldest first) into fresh arrays (allocates; tests only). */
  getTrace(n = this.traceSize): Float32Array[] {
    const count = Math.min(n, this.traceCount);
    const out: Float32Array[] = [];
    for (let i = count; i > 0; i--) {
      const idx = (this.traceHead - i + this.traceSize) % this.traceSize;
      out.push(Float32Array.from(this.trace[idx]!));
    }
    return out;
  }
}

export { J as JOINT, NJ as JOINT_COUNT };
